import { isBillingOnly } from "../lib/access";
import type { Me } from "../lib/types";

/**
 * 메뉴 구조 (시안 navigation.ts 기준, 기획 문서 "콘솔 메뉴 구조"). 1뎁스 카테고리 + 화면 안 탭.
 * 보이는지는 역할이 아니라 서버가 내려준 권한 코드로 정한다 (서버도 같은 코드로 막는다).
 */
export type Tab = { id: string; label: string };
export type Category = { id: string; label: string; tabs: Tab[] };

const tab = (id: string, label: string): Tab => ({ id, label });
const has = (me: Me, ...p: string[]) => p.some((x) => me.permissions.includes(x));

export function adminNavigation(me: Me): { categories: Category[]; system: Category } {
  const billingOnly = isBillingOnly(me);
  const categories: Category[] = [
    { id: "overview", label: "상황판", tabs: [tab("overview", "요약")] },
    // 시안 06a1acb: 고객사는 목록만. SEO 관리는 고객사 상세 › SEO 탭, SEO 비교는 분석으로.
    { id: "customers", label: "고객사", tabs: [tab("customers", "고객사 목록")] },
    // 시안 6a084bb: 작업 카테고리에 결과 검토함 (게시 결과의 캡처·AI 판정을 직원이 검토).
    // 시안 ae6ed08: 작업 › 원고함 (docs/meta-crew-manuscript-library-design.md). 요청서 29절: 작업 › 정형화 (정형화·검수 작업 목록)
    ...(!billingOnly
      ? [{ id: "runs", label: "작업", tabs: [tab("runs", "실행 이력"), tab("reviews", "결과 검토함"), tab("collections", "수집 이력"), ...(has(me, "MANUSCRIPT_READ") ? [tab("manuscripts", "원고함"), tab("refine", "정형화")] : [])] }]
      : []),
    // 2026-10-20 경쟁 분석 (docs/meta-crew-seo-competition-design.md): 상위 글 vs 우리 글, 자유 분석.
    ...(has(me, "MARKETING_READ") ? [{ id: "marketing", label: "분석", tabs: [tab("marketing", "전체 분석"), tab("seo-comparison", "SEO 비교"), tab("competition", "경쟁 분석")] }] : []),
    { id: "billing-ops", label: "결제", tabs: [tab("billing-ops", "결제 운영")] },
    {
      id: "monitoring",
      label: "모니터링",
      tabs: [
        tab("monitoring", "시스템 지표"),
        // 2026-10-21 요청 25: 머신 카테고리를 없애고 전체 고객사 머신 목록(끊김·업데이트 필요·삭제됨)은 모니터링 › 머신으로. 디바이스는 머신 상세 안에서 본다.
        ...(!billingOnly ? [tab("machines", "머신")] : []),
        ...(has(me, "MARKETING_READ") ? [tab("seo-collection", "SEO 수집")] : []),
        tab("llm", "LLM 사용량"),
        tab("usage", "전체 사용량"),
        ...(has(me, "AUDIT_READ_ALL") ? [tab("audit", "감사 기록")] : []),
      ],
    },
    // 시안 05be2fc (요청서 24절): 관리자 계정과 역할(RBAC)은 "사용자 관리" 카테고리로. 경로(/admin/staff, /admin/roles)는 그대로.
    {
      id: "users",
      label: "사용자 관리",
      tabs: [...(has(me, "USER_MANAGE") ? [tab("staff", "관리자")] : []), ...(me.isStaff && has(me, "SYSTEM_READ") ? [tab("roles", "역할")] : [])],
    },
  ].filter((c) => c.tabs.length > 0);
  const system: Category = {
    id: "system",
    label: "시스템 설정",
    tabs: [
      // 시안 05be2fc: AI 키 · 요금·제재 정책 · 시스템 템플릿 · 수집기 · 버전 배포 (역할·관리자는 사용자 관리로 옮김)
      ...(me.isStaff && has(me, "SYSTEM_READ") ? [tab("ai-keys", "AI 키")] : []),
      // 요청서 30절: API 키 / GPT 플랜(실행기) 선택과 device-auth 로그인. 29-4절: 원고 가이드라인 버전 관리
      ...(me.isStaff && has(me, "SYSTEM_READ") ? [tab("ai-execution", "AI 실행 방식")] : []),
      ...(me.isStaff && has(me, "MANUSCRIPT_READ") ? [tab("guidelines", "원고 가이드라인")] : []),
      ...(has(me, "BILLING_MANAGE", "SYSTEM_READ") ? [tab("settings", "요금·제재 정책")] : []),
      ...(has(me, "TEMPLATE_SYSTEM") ? [tab("templates", "시스템 템플릿")] : []),
      ...(has(me, "COLLECTOR_MANAGE") ? [tab("collectors", "수집기")] : []),
      // 2026-10-21 요청 25: 데스크톱 앱 버전 배포는 시스템 설정으로 (여러 고객사 머신에 한꺼번에)
      ...(has(me, "MACHINE_DEPLOY") ? [tab("deploy", "버전 배포")] : []),
    ],
  };
  // 2026-10-21 요청 27: 시스템 설정은 헤더 아이콘이 아니라 좌측 메뉴의 마지막 카테고리로 둔다.
  return { categories: system.tabs.length > 0 ? [...categories, system] : categories, system };
}

export function customerNavigation(me: Me): Category[] {
  // 시안 d074369: 고객 웹 5개(현황·머신·분석·결제·조직). 직원이 고객사를 열면 작업·사용량·고객사 설정이 더해진다 (권한 코드로).
  return [
    { id: "overview", label: "현황", tabs: [tab("overview", "요약")] },
    ...(has(me, "TASK_DETAIL")
      ? [{ id: "tasks", label: "작업", tabs: [tab("tasks", "실행 이력"), tab("task-schedules", "예약 · 반복"), tab("reviews", "결과 검토함")] }]
      : // 2026-10-16 고객 발행: 고객 웹의 "작업" 부활. 자기 요청에서 비롯된 작업만 보인다.
        has(me, "PUBLISH_READ")
        ? [{ id: "tasks", label: "작업", tabs: [tab("tasks", "작업 목록")] }]
        : []),
    // 2026-10-21 요청 25: 디바이스 탭 없음 — 폰은 머신 상세 › 디바이스에서 본다
    { id: "machines", label: "머신", tabs: [tab("machines", "머신 목록")] },
    ...(has(me, "MARKETING_READ") ? [{ id: "marketing", label: "분석", tabs: [tab("marketing", "분석")] }] : []),
    ...(has(me, "USAGE_READ") ? [{ id: "usage", label: "사용량", tabs: [tab("usage", "사용량")] }] : []),
    ...(has(me, "BILLING_READ") ? [{ id: "billing", label: "결제", tabs: [tab("billing", "결제")] }] : []),
    {
      id: "organization",
      label: "조직",
      tabs: [
        ...(has(me, "MEMBER_READ") ? [tab("members", "회원")] : []),
        ...(me.kind === "user" ? [tab("profile", "내 프로필")] : []),
        ...(has(me, "AUDIT_READ") ? [tab("audit", "감사 기록")] : []),
        // 2026-10-21 RBAC: 고객사 전용 역할 (소유자·관리자가 만들고 회원에게 묶는다). 관리자가 고객사를 열었을 때도 같은 화면.
        ...(me.kind === "user" && (has(me, "ROLE_MANAGE") || (me.isStaff && has(me, "SYSTEM_READ"))) ? [tab("customer-roles", "역할")] : []),
      ],
    },
  ].filter((c) => c.tabs.length > 0);
}

/**
 * 데스크톱 앱(Electron, 머신 계정) 메뉴 (시안 d074369): 현황 · 머신 · 분석 · 이 PC 설정.
 * 결제·조직·작업은 없다. "이 PC 설정"은 window.metaCrewNative 가 있을 때만 (웹에서는 메뉴·경로 모두 없음).
 */
export function desktopNavigation(me: Me, nativeAvailable: boolean): Category[] {
  if (me.kind !== "machine") return [];
  return [
    { id: "overview", label: "현황", tabs: [tab("overview", "현황")] },
    { id: "machines", label: "머신", tabs: [tab("machine", "머신")] },
    { id: "marketing", label: "분석", tabs: [tab("marketing", "분석")] },
    ...(nativeAvailable ? [{ id: "local", label: "이 PC 설정", tabs: [tab("settings", "이 PC 설정")] }] : []),
  ];
}

/** 데스크톱 전용 화면 (웹에서는 접근 자체를 막는다). */
export const DESKTOP_ONLY_SCREENS = new Set(["settings"]);

export function findCategory(categories: Category[], screen: string): Category | undefined {
  return categories.find((c) => c.tabs.some((t) => t.id === screen));
}
